import { IconAI } from "@/assets/icons/geist/IconAI";
import { IconChartNoAxesCombined } from "@/assets/icons/geist/IconChartNoAxesCombined";
import { IconGitpodEngraved } from "@/assets/icons/geist/IconGitpodEngraved";
import { IconGrid } from "@/assets/icons/geist/IconGrid";
import { IconPlusCircle } from "@/assets/icons/geist/IconPlusCircle";
import { type Size } from "@/assets/icons/geist/Size";
import { SidebarAgentExecutionList } from "@/components/ai/SidebarAgentExecutionList";
import { SidebarSessionList } from "@/components/ai/SidebarSessionList";
import { ArchivedSidebarContent } from "@/components/archive/ArchivedSidebar";
import { DesktopTitlebar } from "@/components/desktop/DesktopTitlebar";
import { Button } from "@/components/flexkit/Button";
import { Resizable, ResizableHandle } from "@/components/flexkit/Resizable";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { KeyboardKey } from "@/components/KeyboardKey";
import { Nudges } from "@/components/nudges/Nudges";
import { OnboardingSidebarSection } from "@/components/onboarding/OnboardingSidebarSection";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { DesktopTrafficLights } from "@/components/sidebar/DesktopTrafficLights";
import { SidebarButton } from "@/components/SidebarButton";
import { Tooltip } from "@/components/Tooltip";
import { Flags } from "@/feature-flags";
import { useDesktop } from "@/hooks/use-desktop";
import { useFlag } from "@/hooks/use-flag";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useMembership } from "@/hooks/use-membership";
import { useNewEnvironmentShortcut } from "@/hooks/use-open-env-modal-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { useSidebarToggleShortcut } from "@/hooks/use-sidebar-toggle-shortcut";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { useTheme } from "@/hooks/use-theme";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { CreateEnvironmentModal } from "@/routes/environments/create/CreateEnvironmentModal";
import { SidebarEnvironmentList } from "@/routes/environments/SidebarEnvironmentList";
import { getPlatformModifierKey } from "@/utils/platform";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { Archive, CirclePlus, MenuIcon, PanelLeftClose, PanelLeftOpen, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FC, type PropsWithChildren, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useHasArchivedEnvironments } from "@/queries/environment-queries";

const SIDEBAR_STATE_KEY = "sidebar-state";

// todo(ft): delete once new macOS version properly rolls out
const useIsGlobalHeaderEnabled = () => {
    const { value: isGlobalHeaderEnabled } = useFlag(Flags.IsGlobalHeaderEnabled, false);
    const { appMainInfo, isDesktop } = useDesktop();
    if (!isDesktop) {
        return isGlobalHeaderEnabled;
    }

    const desktopVersion = appMainInfo?.version ?? "0.0.0";
    const isNewVersion = desktopVersion > "20250524.309" || desktopVersion === "0.0.1"; // 0.0.1 is the dev version (see package.json)

    return isNewVersion && isGlobalHeaderEnabled;
};

type Props = {
    CustomSidebar?: React.ComponentType<{ className?: string; maxWidth?: number; minWidth?: number }>;
    noPadding?: boolean;
    CustomContent?: FC;
};
export const SidebarLayout: FC<Props> = ({ CustomSidebar, noPadding, CustomContent }) => {
    const { isDesktop } = useDesktop();
    useStreamEvents();

    const enableGlobalHeader = useIsGlobalHeaderEnabled();

    const [isDesktopLocked, setIsDesktopLocked] = useState(() => {
        const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
        if (savedState === "unlocked") {
            return false;
        }

        return true;
    });
    const isNoSidebar = CustomSidebar === NoSidebar;

    const [showNewEnvModal, setShowNewEnvModal] = useState(false);
    useNewEnvironmentShortcut(() => {
        setShowNewEnvModal(true);
    });
    const [isHovered, setIsHovered] = useState(false);
    const [isArchiveView, setIsArchiveView] = useState(false);
    const canHoverRef = useRef(true);

    const [playTransition, setPlayTransition] = useState(false);
    const timeoutRef = useRef<number>();
    const doPlayTransition = useCallback(() => {
        setPlayTransition(true);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setPlayTransition(false);
        }, 200) as unknown as number; // reset the playTransition after 200ms, so that the sidebar can transition out smoothly
    }, []);

    const toggleDesktopLock = useCallback(() => {
        const newState = !isDesktopLocked;
        localStorage.setItem(SIDEBAR_STATE_KEY, newState ? "locked" : "unlocked");
        setIsDesktopLocked(newState);
        setIsHovered(false);
        doPlayTransition();

        if (!newState) {
            canHoverRef.current = false;
            setTimeout(() => {
                canHoverRef.current = true;
            }, 300);
        }
    }, [isDesktopLocked, doPlayTransition]);

    // This ensures that we trigger a re-render when the principal changes - this is needed to
    // ensure we re-render when the user is updated.
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal]);

    const location = useLocation();
    // when app.tsx gives us showCreateModal, we know that the URL hash has a relevant context URL for us
    useEffect(() => {
        if (location.state?.showCreateModal) {
            setShowNewEnvModal(true);
        }
    }, [location]);

    useSidebarToggleShortcut(() => {
        if (isNoSidebar) {
            return;
        }

        if (isMobileViewport) {
            setMobileMenuOpen(!mobileMenuOpen);
        } else {
            toggleDesktopLock();
        }
    });

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isMobileViewport = useIsMobileViewport();
    const showSidebar = isMobileViewport && !CustomSidebar ? mobileMenuOpen : isDesktopLocked || isHovered;
    const isLocked = isMobileViewport && !CustomSidebar ? mobileMenuOpen : isDesktopLocked;

    const sidebarClassName = cn(
        { "transition-all duration-200 ease-in-out": playTransition },
        { "opacity-0": !showSidebar },
        { "duration-0": isMobileViewport },
    );
    const maxWidth = !showSidebar ? 0 : undefined; // if the sidebar is not shown, we set the minWidth to 0 so that it doesn't take up space
    const minWidth = !showSidebar ? 0 : undefined;

    const showHoverHandle = !isDesktopLocked && !isNoSidebar && !isMobileViewport;

    return (
        <div className="app-background-resetting h-full">
            {/* Skip to main content link for screen readers */}
            <a
                href="#main-content"
                className="sr-only left-[50%] top-1 z-[9999] rounded bg-surface-01 text-base text-content-primary shadow-lg outline-none ring-1 focus:not-sr-only focus:absolute focus:px-3 focus:py-1"
            >
                Skip to main content
            </a>
            <div className="app-background flex size-full flex-col overflow-hidden">
                {/* we only show the titlebar if we're not also showing a header, because in that case, the <Header> renders its own webkit-app-region */}
                {isDesktop && !enableGlobalHeader && <DesktopTitlebar showLegacyTitlebar={true} />}
                {isDesktop && enableGlobalHeader && isNoSidebar && <DesktopTitlebar showLegacyTitlebar={false} />}
                {isMobileViewport && !isNoSidebar && enableGlobalHeader && (
                    <header className="px-4 pb-1 pt-4">
                        <HeaderMobile
                            handleToggleVisibility={() => setMobileMenuOpen(!mobileMenuOpen)}
                            isMenuOpen={mobileMenuOpen}
                            handleOpenNewEnvModal={() => setShowNewEnvModal(true)}
                        />
                    </header>
                )}
                {/* persistent global header */}
                {!isMobileViewport && !isNoSidebar && enableGlobalHeader && (
                    <Header toggleLock={toggleDesktopLock} isLocked={isLocked} showSidebarToggle={!isNoSidebar} />
                )}
                <div className={cn("flex w-full grow flex-row overflow-hidden")}>
                    <div
                        className="relative flex-shrink-0"
                        onMouseEnter={() => {
                            if (!isDesktopLocked && canHoverRef.current && !isMobileViewport) {
                                setIsHovered(true);
                                doPlayTransition();
                            }
                        }}
                        onMouseLeave={() => {
                            setIsHovered(false);
                            setPlayTransition(false);
                        }}
                    >
                        <div
                            className={cn(
                                "h-full overflow-hidden",
                                { "app-background fixed z-50": isHovered && !isLocked }, // we also depend on isHovered because we don't want to make the position fixed when the sidebar is just transitioning out
                                { "pt-2": !enableGlobalHeader },
                                { "pt-8": isDesktop && (!enableGlobalHeader || !isLocked) },
                            )}
                            data-track-location={TrackLocations.Sidebar}
                        >
                            {CustomSidebar ? (
                                <CustomSidebar className={sidebarClassName} maxWidth={maxWidth} minWidth={minWidth} />
                            ) : (
                                <Sidebar
                                    isArchiveView={isArchiveView}
                                    onArchiveViewChange={setIsArchiveView}
                                    toggleLock={toggleDesktopLock}
                                    isLocked={isLocked}
                                    handleOpenNewEnvModal={() => setShowNewEnvModal(true)}
                                    enableGlobalHeader={enableGlobalHeader}
                                    className={sidebarClassName}
                                    maxWidth={maxWidth}
                                    minWidth={minWidth}
                                />
                            )}
                        </div>
                        <div
                            className={cn("h-full w-2 cursor-pointer", {
                                hidden: !showHoverHandle,
                            })} // we only help hovering on desktop and when there is content to reveal
                        />
                    </div>
                    <div
                        className={cn(
                            "size-full flex-grow overflow-hidden p-2",
                            {
                                "opacity-95 blur-sm backdrop-blur-md transition-all duration-300 ease-in-out":
                                    !isDesktopLocked && isHovered,
                            },
                            { "pl-0": showHoverHandle }, // on desktop, this space is taken up by the "hover handle" above
                        )}
                    >
                        {CustomContent ? (
                            <CustomContent />
                        ) : (
                            <MainContent noPadding={noPadding}>
                                <Outlet />
                            </MainContent>
                        )}
                    </div>
                    <div className="fixed bottom-0 left-0">
                        <Nudges />
                    </div>
                </div>
            </div>

            <CreateEnvironmentModal
                open={showNewEnvModal}
                onClose={() => setShowNewEnvModal(false)}
                data-track-location={TrackLocations.SidebarCreateEnvironmentModal}
            />
        </div>
    );
};

export const NoSidebar: FC = () => {
    return <div />;
};

export const ResizableSidebar: FC<{
    children: ReactNode;
    className?: string;
    maxWidth?: number;
    minWidth?: number;
}> = ({ children, className, maxWidth, minWidth }) => {
    return (
        <Resizable
            initialWidth={300}
            maxWidth={maxWidth ?? 500}
            minWidth={minWidth ?? 296}
            autoSaveId="sidebar-width"
            className={cn("h-full", className)}
            fullWidthThreshold={768}
        >
            {children}
        </Resizable>
    );
};

export const Sidebar: FC<{
    isArchiveView: boolean;
    onArchiveViewChange: (isArchive: boolean) => void;
    toggleLock: () => void;
    isLocked: boolean;
    handleOpenNewEnvModal: () => void;
    enableGlobalHeader: boolean;
    className?: string;
    maxWidth?: number;
    minWidth?: number;
}> = ({
    isArchiveView,
    onArchiveViewChange,
    toggleLock,
    isLocked,
    handleOpenNewEnvModal,
    enableGlobalHeader,
    className,
    maxWidth,
    minWidth,
}) => {
    return (
        <ResizableSidebar maxWidth={maxWidth} minWidth={minWidth} className={className}>
            <div data-testid="sidebar" className="flex size-full flex-col pb-[6px] pl-2 pt-0.5">
                {isArchiveView ? (
                    <ArchivedSidebarContent onArchiveViewChange={onArchiveViewChange} />
                ) : (
                    <>
                        {!enableGlobalHeader && (
                            <div className="hidden px-2 md:block">
                                <SidebarHeader
                                    toggleLock={toggleLock}
                                    isLocked={isLocked}
                                    handleOpenNewEnvModal={handleOpenNewEnvModal}
                                />
                            </div>
                        )}
                        <ScrollArea orientation="vertical" className="mr-0.5 flex-grow pt-2">
                            <SidebarContent handleOpenNewEnvModal={handleOpenNewEnvModal} />
                        </ScrollArea>
                        <div className="pb-2">
                            <SidebarFooter onArchiveViewChange={onArchiveViewChange} />
                        </div>
                    </>
                )}
            </div>
            <ResizableHandle className="top-[16px] mr-[1px] h-[calc(100dvh-32px)]" />
        </ResizableSidebar>
    );
};

type SidebarHeaderProps = PropsWithClassName<{
    toggleLock: () => void;
    isLocked: boolean;
    handleOpenNewEnvModal: () => void;
}>;
/**
 * @deprecated use Header instead
 */
const SidebarHeader: FC<SidebarHeaderProps> = ({ toggleLock, isLocked, className }) => {
    const setLocked = useCallback(
        (open: boolean) => {
            if (open && !isLocked) {
                toggleLock();
            }
        },
        [toggleLock, isLocked],
    );

    return (
        <div className={cn("flex flex-row justify-between gap-2", className)}>
            <div className="flex min-w-16">
                <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} onOpenChange={setLocked} />
            </div>

            <div className="flex gap-2">
                <Tooltip
                    content={
                        <div className="flex flex-row items-center gap-2">
                            <span>{isLocked ? "Close sidebar" : "Lock sidebar open"}</span>
                            <KeyboardKey className="bg-surface-invert-tertiary">[</KeyboardKey>
                        </div>
                    }
                >
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={toggleLock}
                        LeadingIcon={isLocked ? IconPanelLeftClose : IconPanelLeftOpen}
                        aria-label={isLocked ? "Close sidebar" : "Lock sidebar open"}
                    />
                </Tooltip>
            </div>
        </div>
    );
};

const IconPanelLeftOpen: FC<{ size: Size }> = ({ size }) => {
    const sizeMap = { sm: 16, base: 20, lg: 24 };
    return <PanelLeftOpen size={sizeMap[size]} />;
};

const IconPanelLeftClose: FC<{ size: Size }> = ({ size }) => {
    const sizeMap = { sm: 16, base: 20, lg: 24 };
    return <PanelLeftClose size={sizeMap[size]} />;
};

const IconX: FC<{ size: Size }> = ({ size }) => {
    const sizeMap = { sm: 16, base: 20, lg: 24 };
    return <XIcon size={sizeMap[size]} />;
};

const IconMenu: FC<{ size: Size }> = ({ size }) => {
    const sizeMap = { sm: 16, base: 20, lg: 24 };
    return <MenuIcon size={sizeMap[size]} />;
};

type HeaderProps = {
    toggleLock: () => void;
    isLocked: boolean;
    showSidebarToggle: boolean;
};
const Header: FC<HeaderProps> = ({ toggleLock, isLocked, showSidebarToggle }) => {
    return (
        <div className="flex h-9 flex-shrink-0 flex-row items-center justify-between gap-2 border-b-0.5 border-border-base bg-[rgba(0,0,0,0.05)] px-5">
            <div className="flex gap-2">
                <DesktopTrafficLights />
                {showSidebarToggle && (
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                <span>{isLocked ? "Close sidebar" : "Lock sidebar open"}</span>
                                <KeyboardKey className="bg-surface-invert-tertiary">[</KeyboardKey>
                            </div>
                        }
                    >
                        <Button
                            variant="link"
                            size="md"
                            onClick={toggleLock}
                            LeadingIcon={isLocked ? IconPanelLeftClose : IconPanelLeftOpen}
                            className="text-content-primary focus-visible:outline-content-primary"
                            aria-label={isLocked ? "Close sidebar" : "Lock sidebar open"}
                        />
                    </Tooltip>
                )}
                <div className="flex min-w-16">
                    <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} />
                </div>
            </div>
            <div className="h-full flex-grow [-webkit-app-region:drag]" />
        </div>
    );
};

const HeaderMobile: FC<
    PropsWithClassName<{ isMenuOpen: boolean; handleToggleVisibility: () => void; handleOpenNewEnvModal: () => void }>
> = ({ handleToggleVisibility, isMenuOpen, className, handleOpenNewEnvModal }) => {
    const location = useLocation();
    useEffect(() => {
        if (isMenuOpen) {
            handleToggleVisibility();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on path change. This effect makes sure that when the user navigates to a new page, the page shows up instead of just the sidebar.
    }, [location.pathname]);

    return (
        <>
            <div className={cn("flex flex-row justify-between gap-2", className)}>
                <div className="flex flex-row items-center gap-2">
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                <span>{isMenuOpen ? "Close menu" : "Open menu"}</span>
                                <KeyboardKey className="bg-surface-invert-tertiary">[</KeyboardKey>
                            </div>
                        }
                    >
                        <Button
                            variant={"link"}
                            size="md"
                            onClick={handleToggleVisibility}
                            className={"text-content-primary"}
                            LeadingIcon={isMenuOpen ? IconX : IconMenu}
                            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                        />
                    </Tooltip>
                    {!isMenuOpen && (
                        <div className="flex min-w-16">
                            <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} />
                        </div>
                    )}
                </div>

                {!isMenuOpen && (
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                <span>New environment</span>
                                <KeyboardKey className="bg-surface-invert-tertiary">
                                    {getPlatformModifierKey()} + O
                                </KeyboardKey>
                            </div>
                        }
                    >
                        <Button
                            id="new-environment"
                            variant="link"
                            size="md"
                            className={"text-content-primary"}
                            onClick={handleOpenNewEnvModal}
                            aria-label="New environment"
                        >
                            <CirclePlus size={20} />
                        </Button>
                    </Tooltip>
                )}
            </div>
        </>
    );
};

export const SidebarFooter: FC<{
    onArchiveViewChange?: (isArchive: boolean) => void;
}> = ({ onArchiveViewChange }) => {
    const { effectiveTheme: variant } = useTheme();
    const { value: isArchivingEnabled } = useFlag(Flags.EnableEnvironmentArchiving, false);
    const { hasArchivedEnvironments } = useHasArchivedEnvironments();

    // WIP: Archive feature
    // The Archive button toggles the sidebar view to show archived environments.
    // The actual archiving functionality will be implemented in a future update.
    if (isArchivingEnabled && onArchiveViewChange) {
        return (
            <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                <SidebarButton
                    icon={<Archive size="lg" />}
                    label="Archive"
                    onClick={() => onArchiveViewChange(true)}
                    showBadge={hasArchivedEnvironments}
                />
            </div>
        );
    }

    // Default state: Show the Gitpod logo centered at the bottom of the sidebar
    return (
        <div className="mt-4 flex min-h-6 w-full flex-col items-center justify-center gap-[4px]">
            <IconGitpodEngraved variant={variant} className="block h-6 w-6 shadow-engraving" />
        </div>
    );
};

const SidebarContent: FC<{ handleOpenNewEnvModal: () => void }> = ({ handleOpenNewEnvModal }) => {
    const location = useLocation();
    const { value: isOnaEnabled } = useFlag(Flags.OnaAIEnabled, false);
    const { value: isInsightsDisabled } = useFlag(Flags.InsightsDisabled, false);
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    return (
        <>
            <div className="flex flex-col gap-1 pl-2">
                <OnboardingSidebarSection />
                {isOnaEnabled && (
                    <SidebarButton
                        icon={<IconAI size="lg" />}
                        label="Home"
                        active={location.pathname === "/ai"}
                        to="/ai"
                    />
                )}
                <SidebarButton
                    icon={<IconGrid size="lg" />}
                    label="Projects"
                    active={location.pathname === "/projects"}
                    to="/projects"
                />
                {!isInsightsDisabled && isOrgAdmin && (
                    <SidebarButton
                        icon={<IconChartNoAxesCombined size="lg" />}
                        label="Insights"
                        active={location.pathname === "/insights"}
                        to="/insights"
                    />
                )}
                <Tooltip
                    content={
                        <div className="flex flex-row items-center gap-2">
                            <span>New environment</span>
                            <KeyboardKey className="bg-surface-invert-tertiary">
                                {getPlatformModifierKey()} + O
                            </KeyboardKey>
                        </div>
                    }
                >
                    <SidebarButton
                        icon={<IconPlusCircle size="lg" />}
                        label="New environment"
                        onClick={handleOpenNewEnvModal}
                    />
                </Tooltip>
                <div className="mx-4 my-3 border-t-[1px] border-surface-invert/10" />
                <Lists />
            </div>
        </>
    );
};

const Lists = () => {
    const { value: isOnaEnabled } = useFlag(Flags.OnaAIEnabled, false);
    const { value: isOnaPrototypeUIEnabled } = useFlag(Flags.OnaPrototypeUIEnabled, false);

    if (!isOnaEnabled) {
        return <SidebarEnvironmentList />;
    }
    if (isOnaPrototypeUIEnabled) {
        return <SidebarSessionList />;
    } else {
        return (
            <>
                <SidebarAgentExecutionList />
                <SidebarEnvironmentList />
            </>
        );
    }
};

type MainContentProps = PropsWithChildren<{ noPadding?: boolean }>;
export const MainContent: FC<MainContentProps> = ({ children, noPadding }) => {
    const Comp = noPadding ? "div" : ScrollArea;
    return (
        <main
            id="main-content"
            className={cn(
                "size-full overflow-hidden rounded-lg border-0.5 border-border-base bg-surface-01 p-0.5 shadow-content-surface dark:shadow-none",
                { "p-0": noPadding },
            )}
        >
            <Comp
                orientation="both"
                className={cn("size-full max-w-full", {
                    "p-0": noPadding,
                    "p-6": !noPadding,
                })}
            >
                {children}
            </Comp>
        </main>
    );
};
